<template>

  <v-container class="pa-0 ma-0 step delivery-step-head-to-shipper" v-if="route && order && shipperLocation">

    <!------------------------------------------>
    <!-- Provider order sync validation/guard -->
    <!------------------------------------------>
    <delivery-order-provider-sync
        v-if="isProviderLinked && isStepActive"
        class="mb-6"
        :order="order"
        :blockUntilSynced="false"
        @updated="handleProviderOrderUpdated" />

    <!-------------------->
    <!-- Driver message -->
    <!-------------------->
    <v-container class="driver-message-container mt-1 mb-6 pb-5" v-if="driverMessageItems.length > 0">
      <v-row>
        <v-col>
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="amber darken-2" class="pr-2">mdi-message-alert-outline</v-icon>
              <div class="driver-message-title">
                Message from Dispatcher
              </div>
            </div>
            <div class="d-flex align-center" style="color:#727272;" v-if="driverMessageItems[0].is_confirmed">
              <v-icon style="font-size:18px;color:#727272;" class="pr-2">mdi-eye-outline</v-icon>
              {{ formatDateTime(driverMessageItems[0].confirmed_at) }}
            </div>
            <div v-else-if="!hasCompleted">
              <v-btn
                  small
                  outlined
                  color="green darken-1"
                  @click="confirmDriverMessage(driverMessageItems[0])">
                Confirm Seen
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-1">
        <v-col>
          <div class="driver-message-text">
            {{ driverMessageItems[0].message }}
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Confirmation code instructions -->
    <v-row class="pa-0 ma-0">
      <v-col sm="12" class="ma-0 pa-0 d-flex flex-column align-left justify-center">
        <div class="content-title code-entry-text">
          <span v-if="requireConfirmationCode">Locate the order and enter the confirmation code below.</span>
          <span v-else>Locate the order for pickup.</span>
        </div>
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0 mt-5 mb-3">
      <v-col sm="12" class="ma-0 pa-0 d-flex flex-column align-left justify-center">
        <div class="content-accent">
          <span style="font-size:18px;color:#9c9c9c;" class="mr-2">Order</span>
          <span style="font-size:23px">{{ order.thirdparty_order_number }}</span>
        </div>
      </v-col>
    </v-row>

    <!-- Confirmation code entry -->
    <v-row no-gutters class="pa-0 ma-0 confirm-code-entry" v-if="requireConfirmationCode">
      <v-col cols="8" class="ma-0 pa-0 d-flex align-center">
        <div>
          <div class="input-label-text">
            Confirmation code
          </div>
          <v-text-field v-if="!isOrderConfirmed && !hasCompleted"
              outlined
              maxlength="6"
              hide-details="auto"
              style="min-width:170px;max-width:200px;"
              class="mt-2"
              :clearable="!isOrderConfirmed"
              :disabled="isOrderConfirmed || isConfirmCodeProcessing"
              @click:clear="clearCodeConfirmErrorMessage"
              v-model.trim="confirmationCodeEntry" />
          <div v-else-if="isOrderConfirmed">
            <transition name="fade" appear>
              <div class="mt-3 d-flex align-center">
                <span style="font-size:1.08rem;color:#4caf50;">CONFIRMED</span>
                <v-icon color="green" class="ml-2">mdi-check-circle</v-icon>
              </div>
            </transition>
          </div>
        </div>
        <div class="mt-8 ml-4" v-if="!isOrderConfirmed && !hasCompleted">
          <v-progress-circular indeterminate :size="26" color="primary" v-if="isConfirmCodeProcessing" />
          <v-btn height="42" width="76" color="primary" @click="confirmOrder" v-else>
            OK
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <!-- (error message) -->
    <v-row class="pa-0 ma-0 mt-3">
      <v-col sm="12" class="py-0 d-flex flex-column align-left justify-center">
        <div class="error-message" v-if="confirmOrderError">
          {{ confirmOrderError.message }}
        </div>
      </v-col>
    </v-row>

    <!-- Confirmation Badge -->
    <template v-if="isOrderConfirmed || !requireConfirmationCode">
      <transition name="fade" appear>
        <v-row class="pa-0 ma-0">
          <v-col cols="12" class="py-0 d-flex flex-column align-left justify-center">
            <div>
              <!-- Pickup confirmation badge modal -->
              <v-dialog :fullscreen="$root.isMobileView()" persistent v-model="isPickupConfirmationBadgeOpen">
                <!-- Activator button -->
                <template v-slot:activator="{ on, attrs }">
                  <v-btn text color="amber darken-2" class="mt-2" v-bind="attrs" v-on="on">
                    <v-icon class="icon-open-order-checklist mr-2">mdi-shield-star-outline</v-icon>
                    <span>Show Confirmation Badge</span>
                  </v-btn>
                </template>
                <!-- Dialog content -->
                <v-card>
                  <delivery-order-pickup-confirmation-badge
                      :order="order"
                      :showConfirmCode="requireConfirmationCode"
                      v-on:close="isPickupConfirmationBadgeOpen = false" />
                </v-card>
              </v-dialog>
            </div>
          </v-col>
        </v-row>
      </transition>
    </template>

    <!-- Verify order items -->
    <v-row class="pa-0 ma-0 mt-10">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="content-title">
          Verify all ordered items are present.
        </div>
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="py-0 d-flex flex-column align-left justify-center">
        <div class="mt-3 d-flex align-center">
          <!-- Order checklist modal -->
          <v-dialog scrollable fullscreen persistent v-model="isOrderChecklistOpen">
            <!-- Activator button -->
            <template v-slot:activator="{ on, attrs }">
              <v-btn text color="primary" class="ml-2" v-bind="attrs" v-on="on" :disabled="!isOrderConfirmed && requireConfirmationCode">
                <v-icon class="icon-open-order-checklist mr-2">mdi-open-in-new</v-icon>
                <span>Order checklist</span>
              </v-btn>
            </template>
            <!-- Dialog content -->
            <v-card class="dialog-content">
              <!-- <v-card-title>Order {{ order.thirdparty_order_number }} Checklist</v-card-title> -->
              <v-card-title style="border-bottom:1px solid #3c3c3c;">
                <v-row>
                  <v-col cols="8" xs="8" sm="8" md="8" lg="8">
                    <div>Order {{ order.thirdparty_order_number }}</div>
                    <div class="sub-title">Item Checklist</div>
                  </v-col>
                  <v-col cols="4" xs="4" sm="4" md="4" lg="4" class="d-flex align-center justify-end">
                    <v-btn large text icon color="primary" class="mb-2" @click="isOrderChecklistOpen = false">
                      <v-icon large>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <delivery-order-checklist
                    :order="order"
                    :checkOffMode="hasCompleted !== null"
                    v-on:closed="isOrderChecklistOpen = false"
                    v-on:completed="confirmItems" />
              </v-card-text>
            </v-card>
          </v-dialog>
          <v-icon color="green" class="icon-checklist-confirmed" v-if="itemsConfirmed">mdi-check-circle</v-icon>
        </div>
      </v-col>
    </v-row>

    <!-- E-sign pickup (driver) -->
    <v-row class="pa-0 ma-0 mt-6 mb-14">
      <v-col cols="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center" v-if="!hasDriverSigned">
        <div class="pb-3 disclaimer-text">I have collected and verified the order.</div>
        <v-btn
            color="light-green darken-2"
            width="260"
            :disabled="!isOrderConfirmed && requireConfirmationCode"
            @click="recordDriverSignature">
          <v-progress-circular indeterminate :size="20" color="white" v-if="isRecordingSignature" />
          <template v-else>
            <v-icon color="white" class="mr-1">mdi-draw</v-icon>
            <span class="mr-3">Click to e-sign</span>
          </template>
        </v-btn>
      </v-col>
      <v-col cols="12" class="pa-0 ma-0 d-flex flex-column align-center justify-center" v-else>
        <div class="mt-4 signature-area" v-if="driverSignature">
          <div class="image-container">
            <img style="max-width:50%;max-height:50%;" :src="driverSignature" />
          </div>
        </div>
        <div class="mt-2">
          <span style="color:#4caf50;">DRIVER SIGNED</span>
          <v-icon color="green" class="pl-2">mdi-check-circle</v-icon>
        </div>
      </v-col>
    </v-row>

    <!-- Complete step -->
    <v-row class="mt-5 mx-0 pa-0 complete-step">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <v-btn color="primary" v-if="isPickedUpProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompleted">
          Picked-up at {{ formatDateTime(hasCompleted) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            buttonText="Order Picked-up"
            confirmTitle="Order has been picked-up."
            :confirmMessageText="(isTestRoute) ? '' : 'This will notify all relevant parties.'"
            :disabled="(requireConfirmationCode && !isOrderConfirmed) || !hasDriverSigned"
            @confirmed="completeStep" />
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
import DoubleClickConfirmButton from '@/components/app/DoubleClickConfirmButton'
import DeliveryOrderProviderSync from '@/components/deliveries/DeliveryOrderProviderSync'
import DeliveryOrderPickupConfirmationBadge from '@/components/deliveries/DeliveryOrderPickupConfirmationBadge'
import DeliveryOrderChecklist from '@/components/deliveries/DeliveryOrderChecklist'

export default {
  name: 'DeliveryStepPickupOrder',

  components: {
    DoubleClickConfirmButton,
    DeliveryOrderProviderSync,
    DeliveryOrderPickupConfirmationBadge,
    DeliveryOrderChecklist
  },

  props: {
    route: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    stepNumber: {
      type: Number,
      required: true
    },
    isStepActive: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      // ------ Driver messages
      isMessageDataLoading: false,
      isConfirmingMessage: false,
      driverMessageData: null,
      // ------ Checklist handling
      isOrderChecklistOpen: false,
      confirmItemsError: null,
      // ------ Confirm code handling
      isConfirmCodeProcessing: false,
      isConfirmItemsProcessing: false,
      confirmationCodeEntry: null,
      confirmOrderError: null,
      // ------ Confirmation badge
      isPickupConfirmationBadgeOpen: false,
      // ------ Signature handling
      driverSignature: null,
      isRecordingSignature: false,
      // ------ Picked-up handling
      isPickedUpProcessing: false
    }
  },

  computed: {
    isTestRoute () {
      return this.route.is_test
    },
    isProviderLinked () {
      return Boolean(this.order.provider_alias && this.order.thirdparty_order_id)
    },
    org () {
      return this.$store.getters['auth/activeOrg']
    },
    driverMessageItems () {
      return (this.driverMessageData) ? this.driverMessageData.items : []
    },
    requireConfirmationCode () {
      // If already completed, show per history.
      // If not yet completed, show per active org settings.
      if (this.hasCompleted) {
        return this.isOrderConfirmed
      } else {
        return (this.$root.utils.has(this.org, 'settings.pickup_confirm_code_required'))
          ? this.org.settings.pickup_confirm_code_required
          : false
      }
    },
    shipperLocation () {
      return this.order.shipper
    },
    isOrderConfirmed () {
      return (this.order.code_confirmed_at)
        ? true
        : false
    },
    itemsConfirmed () {
      return (this.order.items_checked_at)
        ? true
        : false
    },
    hasDriverSigned () {
      return (this.order.driver_pickup_signed_at)
        ? true
        : false
    },
    hasCompleted () {
      return this.order.picked_up_at
    }
  },

  async beforeMount () {
    // Load driver messages
    await this.loadDriverMessages()

    // Load driver signature, if already saved
    if (this.hasDriverSigned) await this.loadDriverSignature()
  },

  methods: {
    formatAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data)
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D h:mma') : ''
    },

    // Load driver messages for the order
    async loadDriverMessages () {
      this.isMessageDataLoading = true

      try {
        this.driverMessageData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.order.id}/driver-messages`
        })
      } catch (err) {
        // this.dataLoadingError = err.message || err.text || err
        console.error(err)
      } finally {
        this.isMessageDataLoading = false
      }
    },

    handleProviderOrderUpdated (updatedOrder) {
      if (!updatedOrder) return

      Object.assign(this.order, updatedOrder)
      const syncOptions = {
        resource: 'activeDelivery',
        nested: 'orders',
        data: this.order
      }
      this.$store.dispatch('sh/syncData', syncOptions)
      this.$store.dispatch('sh/syncItemEditing', syncOptions)
    },

    // Confirm driver message was seen
    async confirmDriverMessage (messageItem) {
      if (!messageItem) return false
      this.isConfirmingMessage = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order-driver-message/${messageItem.id}/confirm`
        })

        // Reload driver messages
        this.loadDriverMessages()
      } catch (err) {
        // this.dataLoadingError = err.message || err.text || err
        console.error(err)
      } finally {
        this.isConfirmingMessage = false
      }
    },

    async loadDriverSignature () {
      const signature = await this.$root.fetchUserAsset('signature', this.route.driver_user_id)
      this.driverSignature = signature
    },

    // Perform order code confirmation
    async confirmOrder () {
      this.confirmOrderError = null
      this.isConfirmCodeProcessing = true

      try {
        const data = {
          orderId: this.order.id,
          code: this.confirmationCodeEntry
        }
        await this.$store.dispatch('sh/confirmOrderCode', data)
      } catch (err) {
        this.confirmOrderError = err // display error
      } finally {
        this.isConfirmCodeProcessing = false
      }
    },

    // Perform confirm items
    async confirmItems () {
      this.isConfirmItemsProcessing = true

      try {
        const data = {
          orderId: this.order.id
        }
        await this.$store.dispatch('sh/confirmOrderedItems', data)
      } catch (err) {
        this.confirmItemsError = err // display error
      }

      this.isConfirmItemsProcessing = false
      this.isOrderChecklistOpen = false
    },

    clearCodeConfirmErrorMessage () {
      this.confirmOrderError = null
    },

    // Update order with saved signature timestamp
    async recordDriverSignature () {
      this.isRecordingSignature = true

      // Load signature to display
      await this.loadDriverSignature()

      try {
        const data = {
          orderId: this.order.id
        }
        await this.$store.dispatch('sh/recordDriverSignatureAtPickup', data)
      } catch (err) {
        console.log('Error recording driver signature =>', err)
      }

      this.isRecordingSignature = false
    },

    // Set order: picked-up, and complete step
    async completeStep () {
      this.isPickedUpProcessing = true

      try {
        await this.$store.dispatch('sh/setOrderPickedUp', this.order)
        this.$emit('completed')
      } catch (err) {
        console.log('Error setting order: picked-up =>', err)
      }

      this.isPickedUpProcessing = false
    }
  }
}
</script>

<style>
  /* Driver Messages */
  .delivery-step-head-to-shipper .driver-message-container {
    border: 1px solid #FF9F00;
    border-radius: 4px;
    background-color: #252525;
  }
  .delivery-step-head-to-shipper .driver-message-title {
    font-size: 1.04rem;
    color: #9c9c9c;
    text-transform: uppercase !important;
    letter-spacing: 0.7px;
    font-weight: 400;
  }
  .delivery-step-head-to-shipper .driver-message-text {
    color: #ffffff;
  }

  .code-entry-text {
    text-align: left !important;
  }

  .icon-checklist-confirmed {
    margin-left: 6px;
  }

  .delivery-step-head-to-shipper .signature-area {
    width: 100%;
    background-color: #ffffff;
    text-align: center;
    border-radius: 4px;
  }

  .signature-area .image-container {
    padding: 4px 0 0 0;
    text-align: center;
  }

  .dialog-content .sub-title {
    font-size: 0.8em;
    color: #727272;
    text-transform: uppercase !important;
    letter-spacing: 1.7px;
    margin-top: -2px;
  }
</style>
